import {
  Component,
  Input,
  forwardRef,
  signal,
  computed,
  effect,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { BusinessService } from '../../../services/business/business.service';
import { Service } from '../../../shared/interfaces';
import { AuthService } from '../../../services/auth';

@Component({
  selector: 'app-service-chip-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ServiceChipSelectorComponent),
      multi: true,
    },
  ],
  templateUrl: './service-chip-selector.html',
  styleUrl: './service-chip-selector.css',
})
export class ServiceChipSelectorComponent implements ControlValueAccessor {
  @Input() label?: string;
  @Input() required = false;
  @Input() placeholder = 'Buscar y seleccionar servicios...';
  @Input() businessId?: string;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  private businessService = inject(BusinessService);
  private authService = inject(AuthService);

  readonly selectedServices = signal<Service[]>([]);
  readonly availableServices = signal<Service[]>([]);
  readonly searchTerm = signal<string>('');
  readonly isOpen = signal<boolean>(false);
  readonly isLoading = signal<boolean>(false);
  readonly highlightedIndex = signal<number>(-1);

  private searchSubject = new Subject<string>();
  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor() {
    // Setup search with debounce
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isLoading.set(true);
          const businessId =
            this.businessId || this.authService.getUserInfo()?.business_id;
          if (!businessId) {
            this.isLoading.set(false);
            return [];
          }
          return this.businessService.searchEntityByBusinessId(
            businessId,
            'services',
            term,
            1,
            50
          );
        })
      )
      .subscribe({
        next: (services) => {
          this.availableServices.set(services);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });

    // Load services on focus
    effect(() => {
      if (this.isOpen() && this.availableServices().length === 0) {
        this.searchSubject.next('');
      }
    });
  }

  readonly filteredServices = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const available = this.availableServices();
    const selectedIds = new Set(
      this.selectedServices().map((s) => s.serviceId)
    );

    if (!term) {
      return available.filter((s) => !selectedIds.has(s.serviceId));
    }

    return available.filter(
      (s) =>
        !selectedIds.has(s.serviceId) &&
        s.name.toLowerCase().includes(term)
    );
  });

  readonly displayValue = computed(() => {
    return this.selectedServices()
      .map((s) => s.name)
      .join(', ');
  });

  onSearchInput(event: Event) {
    const term = (event.target as HTMLInputElement).value || '';
    this.searchTerm.set(term);
    this.searchSubject.next(term);
    this.highlightedIndex.set(-1);
  }

  onFocus() {
    if (!this.isOpen()) {
      this.isOpen.set(true);
      const businessId =
        this.businessId || this.authService.getUserInfo()?.business_id;
      if (businessId && this.availableServices().length === 0) {
        this.searchSubject.next('');
      }
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 0);
    }
  }

  onBlur(event: FocusEvent) {
    // Check if focus is moving to an element within the component
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget && this.isElementInsideComponent(relatedTarget)) {
      return; // Don't close if focus is moving within the component
    }

    // Delay to allow click events
    setTimeout(() => {
      // Double check that focus is not within the component
      const activeElement = document.activeElement;
      if (activeElement && this.isElementInsideComponent(activeElement)) {
        return;
      }

      if (this.isOpen()) {
        this.isOpen.set(false);
        this.searchTerm.set('');
        this.highlightedIndex.set(-1);
      }
    }, 200);
  }

  private isElementInsideComponent(element: HTMLElement | Element | null): boolean {
    if (!element) return false;
    const componentElement = element.closest('.service-chip-selector-container');
    return componentElement !== null;
  }

  selectService(service: Service) {
    // Check if already selected (by serviceId or by name for temp services)
    const isAlreadySelected = this.selectedServices().some(
      (s) =>
        s.serviceId === service.serviceId ||
        (s.serviceId.startsWith('temp-') && s.name === service.name)
    );

    if (isAlreadySelected) {
      return;
    }

    // Remove any temp service with the same name and add the real one
    const updated = this.selectedServices()
      .filter((s) => !(s.serviceId.startsWith('temp-') && s.name === service.name))
      .concat(service);

    this.selectedServices.set(updated);
    this.updateValue();
    this.searchTerm.set('');
    this.highlightedIndex.set(-1);
    this.searchInput?.nativeElement?.focus();
  }

  removeService(service: Service) {
    const updated = this.selectedServices().filter(
      (s) => s.serviceId !== service.serviceId
    );
    this.selectedServices.set(updated);
    this.updateValue();
  }

  private updateValue() {
    const value = this.selectedServices()
      .map((s) => s.name)
      .join(', ');
    this.onChange(value);
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isOpen()) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.onFocus();
      }
      return;
    }

    const filtered = this.filteredServices();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const currentDown = this.highlightedIndex();
        const nextIndex =
          currentDown < filtered.length - 1 ? currentDown + 1 : 0;
        this.highlightedIndex.set(nextIndex);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const currentUp = this.highlightedIndex();
        const prevIndex =
          currentUp > 0 ? currentUp - 1 : filtered.length - 1;
        this.highlightedIndex.set(prevIndex);
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        const highlighted = this.highlightedIndex();
        if (highlighted >= 0 && highlighted < filtered.length) {
          this.selectService(filtered[highlighted]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.isOpen.set(false);
        this.searchTerm.set('');
        this.highlightedIndex.set(-1);
        break;
    }
  }

  onSearchKeyDown(event: KeyboardEvent) {
    const filtered = this.filteredServices();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const currentDown = this.highlightedIndex();
        const nextIndex =
          currentDown < filtered.length - 1 ? currentDown + 1 : 0;
        this.highlightedIndex.set(nextIndex);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const currentUp = this.highlightedIndex();
        const prevIndex =
          currentUp > 0 ? currentUp - 1 : filtered.length - 1;
        this.highlightedIndex.set(prevIndex);
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        const highlighted = this.highlightedIndex();
        if (highlighted >= 0 && highlighted < filtered.length) {
          this.selectService(filtered[highlighted]);
        } else if (filtered.length === 1) {
          this.selectService(filtered[0]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.isOpen.set(false);
        this.searchTerm.set('');
        this.highlightedIndex.set(-1);
        break;
    }
  }

  private scrollToHighlighted() {
    setTimeout(() => {
      const element = document.querySelector(
        `[data-service-index="${this.highlightedIndex()}"]`
      );
      element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  isHighlighted(index: number): boolean {
    return this.highlightedIndex() === index;
  }

  closeDropdown() {
    this.isOpen.set(false);
    this.searchTerm.set('');
    this.highlightedIndex.set(-1);
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (!value) {
      this.selectedServices.set([]);
      return;
    }

    // Parse comma-separated service names
    const serviceNames = value
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (serviceNames.length === 0) {
      this.selectedServices.set([]);
      return;
    }

    // Create placeholder services with unique IDs based on name
    // These will be replaced when real services are selected
    const services: Service[] = serviceNames.map((name) => ({
      serviceId: `temp-${name.toLowerCase().replace(/\s+/g, '-')}`,
      name,
      description: '',
      price: 0,
      durationMinutes: 0,
    }));

    this.selectedServices.set(services);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state if needed
  }
}

